import { useState, useEffect } from "react";
import { useCreateTeam, useUpdateTeam, useTeams } from "@/hooks/queries/use-teams";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { Team } from "@/lib/api/types";

interface TeamFormProps {
  team?: Team;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TeamForm({ team, open, onOpenChange }: TeamFormProps) {
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const { data: allTeamsResponse } = useTeams();
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [level, setLevel] = useState<"group" | "product-line" | "product" | "discipline">("group");
  const [parentId, setParentId] = useState<string>("");
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(false);
  const [error, setError] = useState("");

  const isEditing = !!team;

  // Discipline teams are candidates for being a parent of a product
  const disciplineTeams = (allTeamsResponse?.data ?? []).filter(t => t.level === "discipline");

  useEffect(() => {
    if (open) {
      setName(team?.name ?? "");
      setKey(team?.key ?? "");
      setLevel("group");
      setParentId("");
      setKeyManuallyEdited(!!team);
      setError("");
    }
  }, [team, open]);

  // Auto-generate key from name (first 3 letters, uppercase)
  const handleNameChange = (value: string) => {
    setName(value);
    if (!isEditing && !keyManuallyEdited) {
      const autoKey = value
        .replace(/[^a-zA-Z]/g, "")
        .substring(0, 3)
        .toUpperCase();
      setKey(autoKey);
    }
  };

  const handleKeyChange = (value: string) => {
    const sanitized = value.toUpperCase().replace(/[^A-Z]/g, "");
    setKey(sanitized);
    setKeyManuallyEdited(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) return;
    if (!isEditing && !key.trim()) {
      setError("Key is required");
      return;
    }
    if (!isEditing && level === "product" && !parentId) {
      setError("A parent team is required for products");
      return;
    }

    try {
      if (isEditing) {
        await updateTeam.mutateAsync({ id: team.id, name: name.trim() });
      } else {
        await createTeam.mutateAsync({
          name: name.trim(),
          key: key.trim().toUpperCase(),
          level,
          parentId: parentId || null,
        });
      }
      onOpenChange(false);
    } catch (err: unknown) {
      if (err && typeof err === "object" && "data" in err) {
        const data = (err as { data: { message?: string } }).data;
        setError(data?.message || "Failed to save team");
      } else {
        setError("Failed to save team");
      }
    }
  };

  const isPending = createTeam.isPending || updateTeam.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={(e) => void handleSubmit(e)}>
          <DialogHeader>
            <DialogTitle>{isEditing ? "Edit Team" : "Create Team"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {error && <div className="text-sm text-destructive">{error}</div>}
            <div className="grid gap-2">
              <Label htmlFor="name">Team Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => { handleNameChange(e.target.value); }}
                placeholder="Engineering"
                required
              />
            </div>
            {!isEditing && (
              <div className="grid gap-2">
                <Label htmlFor="key">Team Key</Label>
                <Input
                  id="key"
                  value={key}
                  onChange={(e) => { handleKeyChange(e.target.value.replace(/[^A-Za-z]/g, "")); }}
                  placeholder="ENG"
                  pattern="^[A-Z]+$"
                  title="Root team keys must contain only uppercase letters (A-Z)"
                  maxLength={10}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Short identifier using uppercase letters only (e.g., ENG, PRD, COM)
                </p>
              </div>
            )}
            {!isEditing && (
              <div className="grid gap-2">
                <Label htmlFor="level">Team Level</Label>
                <select
                  id="level"
                  value={level}
                  onChange={(e) => {
                    setLevel(e.target.value as typeof level);
                    if (e.target.value !== "product") setParentId("");
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  required
                >
                  <option value="group">Group</option>
                  <option value="product-line">Product Line</option>
                  <option value="product">Product</option>
                  <option value="discipline">Discipline</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  Organizational level for this team
                </p>
              </div>
            )}
            {!isEditing && level === "product" && (
              <div className="grid gap-2">
                <Label htmlFor="parentId">Parent Team <span className="text-destructive">*</span></Label>
                <select
                  id="parentId"
                  value={parentId}
                  onChange={(e) => { setParentId(e.target.value); }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  required
                >
                  <option value="">Select a team...</option>
                  {disciplineTeams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} ({t.key})</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  The discipline team this product belongs to
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { onOpenChange(false); }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !name.trim() || (!isEditing && !key.trim())}>
              {isPending ? "Saving..." : isEditing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
